import type { CSSProperties } from 'react';

import { NodeSchemaEditor } from '../NodeSchemaEditor';
import { useWorkflowEditor } from '../WorkflowEditorContext';
import { WorkflowSchemaEditor } from '../WorkflowSchemaEditor';

const groupHeading: CSSProperties = {
  fontSize: 'var(--fs-sm)',
  fontWeight: 600,
  color: 'var(--text-primary)',
  margin: 0,
  padding: 'var(--sp-2) var(--sp-3)',
  borderBottom: '1px solid var(--border-subtle)',
  background: 'var(--surface-2)',
  letterSpacing: '0.02em',
};

const groupSub: CSSProperties = {
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
  fontWeight: 400,
  marginLeft: 'var(--sp-2)',
};

const groupBody: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-5)',
  padding: 'var(--sp-3)',
};

/**
 * Schemas panel — split into two clearly-labelled groups so authors can
 * tell at a glance which schema they're editing:
 *
 *  • Workflow Schema — the trigger-input contract and the run-output
 *    contract for the whole workflow. Always shown.
 *
 *  • Node Schema — the per-node `inputMapping` (how this node receives
 *    upstream outputs), `outputSchema`, and `outputMapping`. Only shown
 *    when a node is selected via `?nodeKey=…`; otherwise an empty-state
 *    nudges the user to pick a node first.
 *
 * Both groups cut a new workflow version on save, same as before.
 */
export function SchemasPanel({ nodeKey }: { nodeKey: string | null }) {
  const { workflow, getNode } = useWorkflowEditor();
  const selectedNode = getNode(nodeKey);

  // Mutually-exclusive: when a node is selected, the panel is scoped to
  // that node's schema; otherwise it shows the workflow-level contract.
  // Clicking a node on the canvas swaps the view; clicking empty canvas
  // returns to the workflow view.
  if (selectedNode) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <h3 style={groupHeading}>
          Node Schema
          <span style={groupSub}>
            <code>{selectedNode.id}</code>
          </span>
        </h3>
        <div style={groupBody}>
          <NodeSchemaEditor nodeKey={selectedNode.id} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <h3 style={groupHeading}>
        Workflow Schema
        <span style={groupSub}>trigger input + run output</span>
      </h3>
      <div style={groupBody}>
        <WorkflowSchemaEditor workflow={workflow} />
      </div>
    </div>
  );
}
